import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import SeoHead from '../../components/Seo/SeoHead';
import characterApi from '../../services/characterApi';
import universeService from '../../services/universeService';
import { getCharacterProfilePath, getCharactersCatalogPath } from '../../utils/characterPaths';
import { getUserProfilePath } from '../../utils/userPaths';
import styles from './CharactersCatalog.module.css';

const PAGE_SIZE = 24;

const CharacterCard = ({ character }) => {
  const metaParts = [
    character.elseworld?.name,
    character.occupation,
    character.moralAffiliation,
  ].filter(Boolean);

  return (
    <article className={styles.characterCard}>
      <Link to={getCharacterProfilePath(character.id)} className={styles.characterCardMain}>
        {character.avatar ? (
          <img src={character.avatar} alt={character.name} className={styles.characterAvatar} />
        ) : (
          <span className={styles.characterAvatarFallback}>
            {(character.name || '?').charAt(0).toUpperCase()}
          </span>
        )}
        <div className={styles.characterInfo}>
          <h3 className={styles.characterName}>{character.name}</h3>
          {metaParts.length > 0 && (
            <p className={styles.characterMeta}>{metaParts.join(' · ')}</p>
          )}
        </div>
      </Link>
      {character.player?.id && (
        <p className={styles.characterPlayer}>
          Joueur :{' '}
          <Link to={getUserProfilePath(character.player.id)} className={styles.characterPlayerLink}>
            {character.player.pseudo}
          </Link>
        </p>
      )}
    </article>
  );
};

const CharactersCatalog = () => {
  const [universes, setUniverses] = useState([]);
  const [view, setView] = useState('grouped');
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [totalCharacters, setTotalCharacters] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [universeSlug, setUniverseSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    universeService.getUniverses()
      .then((data) => setUniverses(Array.isArray(data) ? data : []))
      .catch(() => setUniverses([]));
  }, []);

  const loadCharacters = useCallback(async (targetPage) => {
    try {
      setLoading(true);
      setError(null);
      const data = await characterApi.getPublicCharactersCatalog(
        targetPage,
        PAGE_SIZE,
        searchQuery,
        universeSlug,
      );
      setView(data.view || 'grouped');
      setGroups(Array.isArray(data.groups) ? data.groups : []);
      setItems(Array.isArray(data.items) ? data.items : []);
      setPagination(data.pagination || null);
      setTotalCharacters(data.totalCharacters ?? 0);
      setPage(targetPage);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des personnages');
      setGroups([]);
      setItems([]);
      setPagination(null);
      setTotalCharacters(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, universeSlug]);

  useEffect(() => {
    void loadCharacters(1);
  }, [loadCharacters]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const handleUniverseChange = (event) => {
    setUniverseSlug(event.target.value);
  };

  const selectedUniverse = universes.find((universe) => universe.slug === universeSlug);
  const hasContent = view === 'grouped'
    ? groups.some((group) => group.items?.length > 0)
    : items.length > 0;

  const seo = {
    metaTitle: 'Personnages — Earth Forum',
    metaDescription: 'Liste des personnages validés sur Earth Forum, par univers.',
    robotsIndex: true,
  };

  if (loading && !hasContent) {
    return (
      <Layout wide>
        <Loading message="Chargement des personnages..." />
      </Layout>
    );
  }

  if (error && !hasContent) {
    return (
      <Layout wide>
        <ErrorMessage message={error} onRetry={() => loadCharacters(1)} />
      </Layout>
    );
  }

  return (
    <Layout wide>
      <SeoHead seo={seo} />
      <div className={styles.content}>
        <Breadcrumb
          items={[
            { name: 'Accueil', url: '/', icon: 'home' },
            { name: 'Personnages', url: null },
          ]}
        />

        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Personnages</h1>
            <p className={styles.subtitle}>
              {totalCharacters} personnage{totalCharacters !== 1 ? 's' : ''} validé{totalCharacters !== 1 ? 's' : ''}
              {universeSlug && selectedUniverse ? ` · ${selectedUniverse.name}` : ' · triés par univers'}
            </p>
          </div>
          <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
            <input
              type="search"
              className={styles.searchInput}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Rechercher un personnage…"
              aria-label="Rechercher un personnage"
            />
            <button type="submit" className={styles.searchButton}>
              Rechercher
            </button>
          </form>
        </header>

        <div className={styles.filters}>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Univers</span>
            <select
              className={styles.filterSelect}
              value={universeSlug}
              onChange={handleUniverseChange}
              aria-label="Filtrer par univers"
            >
              <option value="">Tous les univers (groupés)</option>
              {universes.map((universe) => (
                <option key={universe.id || universe.slug} value={universe.slug}>
                  {universe.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {hasContent ? (
          <>
            {view === 'grouped' ? (
              <div className={styles.groupList}>
                {groups.map((group) => (
                  group.items?.length > 0 && (
                    <section key={group.universe?.slug || group.universe?.name || 'none'} className={styles.universeGroup}>
                      <header className={styles.universeGroupHeader}>
                        <h2 className={styles.universeGroupTitle}>{group.universe?.name}</h2>
                        <span className={styles.universeGroupCount}>
                          {group.total ?? group.items.length} personnage{(group.total ?? group.items.length) !== 1 ? 's' : ''}
                        </span>
                      </header>
                      <div className={styles.characterGrid}>
                        {group.items.map((character) => (
                          <CharacterCard key={character.id} character={character} />
                        ))}
                      </div>
                    </section>
                  )
                ))}
              </div>
            ) : (
              <div className={styles.characterGrid}>
                {items.map((character) => (
                  <CharacterCard key={character.id} character={character} />
                ))}
              </div>
            )}

            {view === 'flat' && pagination && pagination.totalPages > 1 && (
              <nav className={styles.pagination} aria-label="Pagination des personnages">
                <button
                  type="button"
                  className={styles.paginationButton}
                  disabled={page <= 1 || loading}
                  onClick={() => loadCharacters(page - 1)}
                >
                  Précédent
                </button>
                <span className={styles.paginationMeta}>
                  Page {page} sur {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className={styles.paginationButton}
                  disabled={!pagination.hasNext || loading}
                  onClick={() => loadCharacters(page + 1)}
                >
                  Suivant
                </button>
              </nav>
            )}
          </>
        ) : (
          <p className={styles.emptyState}>
            {searchQuery || universeSlug
              ? 'Aucun personnage ne correspond à ces critères.'
              : 'Aucun personnage validé à afficher.'}
          </p>
        )}
      </div>
    </Layout>
  );
};

export default CharactersCatalog;
